<template>
  <div style="max-width: 1200px">
    <ProcessBarModal
      :title="$t('Uploading file on the server')"
      :show-modal="showUploadModal"
      :step="uploadStep"
      :file-name="uploadingFileName"
      :progress="uploadProgress"
      :error-text="uploadError"
      :steps-text="{
        upload: $t('Uploading file on the server'),
        inject: $t('Injecting members'),
        success: $t('Injected members'),
        error: $t('Something went wrong'),
      }"
      @close="() => (showUploadModal = false)"
    />
    <CModal
      centered
      :show.sync="showModal"
      :draggable="false"
      :showFooter="false"
    >
      <CRow class="mt-2">
        <CCol col="12" class="ml-4">
          {{ $t("You are about to modify the list of cover year members.") }}
        </CCol>
      </CRow>
      <CRow class="mt-3 modal-footer">
        <CButton
          class="mz-button v2-mz-btn mr-2"
          color="primary"
          @click="updateCoveryearMembers"
        >
          {{ $t("Confirm") }}
        </CButton>
      </CRow>
    </CModal>
    <CCard class="w-100">
      <CCardHeader>
        {{ $t("Declaring Members") }}
      </CCardHeader>
      <CCardBody>
        <CRow class="m-0 p-0 mb-1 justify-content-center w-100">
          <CCol class="d-flex w-100 m-0 p-0">
            <CRow
              class="ml-1 justify-content-between"
            >
              <CCol>
                <MzSelect
                  searchable
                  class="mb-3"
                  :label="$t('Select cover year')"
                  :options="coveryear_ids"
                  :value.sync="selected_coveryear_id"
                  @input="handleCoverYearIdChange"
                  @update:value="fetchCoverYearMember"
                />
              </CCol>
              <CCol class="" v-if="can_upload_coveryear_members_file">
                <CRow class="text-wrap">
                  {{ $t("Template to download") }}:
                </CRow>

                <CRow class="text-wrap">
                  <a
                    class="template-file"
                    @click="
                      $downloadFile({
                        downloadFileName: 'CoverYear_Adherents.xlsx',
                        url: COVERYEARS_ENDPOINTS.download_coveryear_members_template,
                      })
                    "
                  >
                    CoverYear_Adherents.xlsx
                  </a>
                </CRow>
              </CCol>
            </CRow>
          </CCol>
          <CCol class="d-flex m-0 p-0 justify-content-end">
            <CRow class="d-flex gap-1 m-0 justify-content-end">
              <div class="d-flex p-0">
                <file-dragdrop
                  v-if="can_upload_coveryear_members_file"
                  :disabled="!selected_coveryear_id || !can_upload_coveryear_members_file"
                  :title="$t('Upload cover year members file')"
                  :i18nCTA="$t('click or drop files here')"
                  :i18nDismiss="$t('Dismiss')"
                  :i18nProgress="$t('Upload running...')"
                  :i18nImport="$t('Import data for')"
                  :i18nDontLeaveMessage="$t('Do not leave this page')"
                  :i18nProgressTooltip="
                    $t('Please wait until the upload is completed')
                  "
                  @upload="handleUpload"
                >
                  <template #body-content>
                    {{ $t("Only xlsx files allowed") }}
                  </template>
                  <template #upload-button>
                    <CButton
                      :disabled="
                        !selected_coveryear_id ||
                        !can_upload_coveryear_members_file ||
                        updating_coveryear ||
                        closed_coveryear
                      "
                      color="primary"
                      class="mz-button v2-mz-btn mz-button-custom-radius"
                    >
                      <DotsLoader v-if="uploadingFiles" />
                      <span v-else>
                        {{ $t("Upload") }}
                      </span>
                    </CButton>
                  </template>
                </file-dragdrop>
              </div>
              <div class="d-flex p-0">
                <CButton
                  v-if="hasAccessComplete"
                  class="mz-button v2-mz-btn mz-button-custom-radius"
                  color="primary"
                  :disabled="!selected_coveryear_id || !has_members"
                  @click="downloadFile(selected_coveryear_id)"
                >
                  <span>
                    {{ $t("Download") }}
                  </span>
                </CButton>
              </div>
              <div class="d-flex p-0">
                <CButton
                  v-if="hasAccessComplete"
                  class="mz-button v2-mz-btn"
                  color="primary"
                  @click="() => (showModal = true)"
                  :disabled="
                    !selected_coveryear_id || updating_coveryear || closed_coveryear
                  "
                >
                  <DotsLoader v-if="updating_coveryear" />
                  <span v-else>
                    {{ $t("Save") }}
                  </span>
                </CButton>
              </div>
            </CRow>
          </CCol>
        </CRow>
        <DotsLoader v-if="!coveryear_member_data" />
        <table-component
          v-else
          :key="`declaring_members-${current_page}`"
          :columns="Object.keys(declaringMembersHeaders)"
          :headings="declaringMembersHeaders"
          ref="table_coveryear_member"
          :filter-exclude-columns="[
            'code_gta',
            'member',
            'contact',
            'accounting_code',
            'declarer',
            'prev_ccr_group',
          ]"
          :data="coveryear_member_data"
          :showFilterHelp="false"
          :settings="settings"
        >
          <template slot="code_gta" slot-scope="{ row }">
            {{ row.code_gda || row.id_member }}
          </template>
          <template slot="member" slot-scope="{ row }">
            {{ row.name }}
          </template>
          <template slot="contact" slot-scope="{ row }">
            {{ row.email || "-" }}
          </template>
          <template slot="accounting_code" slot-scope="{ row }">
            {{ row.code_cpt || "-" }}
          </template>

          <template slot="prev_ccr_group" slot-scope="{ row }">
            {{ row.prev_group_ccr || "-" }}
          </template>

          <template slot="ccr_group" slot-scope="{ row }">
            <CInput
              type="text"
              addInputClassess="mz-input"
              :value="row.group_ccr"
              @update:value="
                (value) => handleRowUpdate(row, 'group_ccr', value)
              "
              :disabled="closed_coveryear"
            />
          </template>
          <!-- <template slot="declarer" slot-scope="{ row }">
            <input
              type="checkbox"
              :value.sync="row.declarer"
              :checked="row.declarer"
              @change="handleRowUpdate(row, 'declarer', !row.declarer)"
              :disabled="closed_coveryear"
            />
          </template> -->
          <template slot="filter__declarer">
            <input
              type="checkbox"
              :value.sync="selectAll"
              :checked="selectAll"
              @change="selectAllMembers()"
              :disabled="closed_coveryear"
            />
          </template>
          <template
            v-for="filter in Object.keys(filter_table_data)"
            :slot="`filter__${filter}`"
          >
            <input
              class="form-control mz-input"
              :placeholder="`Filter By ${filter}`"
              v-model="filter_table_data[filter].value"
              @change="filterData()"
            />
          </template>
        </table-component>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { ActiveUser } from "@/services/user";
import { COVERYEARS_ENDPOINTS, CoverYearServices } from "@/services/coveryears";
import Task from "@/services/task";
import ProcessBarModal from "@/components/ProcessBarModal.vue";

export default {
  name: "DeclaringMembers",
  components: { ProcessBarModal },
  data() {
    return {
      COVERYEARS_ENDPOINTS,
      coveryears: [],
      selected_coveryear_id: null, 
      coveryear_data: {},
      coveryear_member_data: [],
      selected_members: {},
      selectAll: false,
      coveryear_ids: [],
      filter_table_data: {
        code_gta: {
          value: "",
          field: "code_gda",
        },
        member: {
          value: "",
          field: "name",
        },
        contact: {
          value: "",
          field: "email",
        },
        accounting_code: {
          value: "",
          field: "code_cpt",
        },
        ccr_group: {
          value: "",
          field: "group_ccr",
        },
      },
      has_members: false,
      isAdministrator: ActiveUser.isAdministrator(),
      showMessage: false,
      updating_coveryear: false,
      current_page: "declaring_members",
      declaringMembersHeaders: {
        code_gta: this.$t("GTA Code"),
        member: this.$t("Member"),
        contact: "Contact",
        accounting_code: this.$t("Accounting Code"),
        ccr_group: this.$t("CCR Group"),
        // declarer: this.$t("Declarer"),
      },
      uploadingFiles: false,
      uploadProgress: 0,
      uploadStep: "upload",
      uploadingFileName: "",
      showUploadModal: false,
      uploadError: "",
      showModal: false,
    };
  },
  async created() {
    await this.fetchCoverYears();
  },
  computed: {
    rates_list_in_rows() {
      const coveryear_list_in_rows = [];
      for (let i = 0; i < this.rates.length; i += this.row_size) {
        const chunk = this.rates.slice(i, i + this.row_size);
        coveryear_list_in_rows.push(chunk);
      }
      return coveryear_list_in_rows;
    },
    hasAccessComplete() {
      return !ActiveUser.isSuperUser();
    },
    can_upload_coveryear_members_file() {
      const allEmpty = this.coveryear_member_data.every(item => item.group_ccr === "");
      return allEmpty && !this.coveryear_data?.coveryear?.date_upload;
    },
    closed_coveryear() {
      if (this.coveryear_data?.coveryear?.date_end) {
        return (
          new Date(this.coveryear_data.coveryear.date_end) < this.currentTime
        );
      }
      return false;
    },
    settings() {
      const sortFunction = this.sortFunction;
      const customSorting = {};
      Object.entries(this.filter_table_data).forEach(([key, field_data]) => {
        customSorting[key] = function (ascending) {
          return function (a, b) {
            return sortFunction(a, b, field_data.field, ascending);
          };
        };
      });
      return { customSorting };
    },
  },
  methods: {
    downloadFile(selected_coveryear_id) {
      this.$downloadFile({
        url: COVERYEARS_ENDPOINTS.download_coveryear_members,
        query: { 
          id_coveryear: selected_coveryear_id,
          language: this.$store.state.language
        },
      })
    },
    sortFunction(a, b, field, ascending) {
      const lastA = a[field]?.toLowerCase();
      const lastB = b[field]?.toLowerCase();

      if (ascending) {
        return lastA >= lastB ? 1 : -1;
      }

      return lastA <= lastB ? 1 : -1;
    },
    sortById(a, b) {
      return new Date(b.id_coveryear) - new Date(a.id_coveryear);
    },
    async fetchCoverYears() {
      this.coveryears = await CoverYearServices.fetchCoverYears();
      this.coveryears.sort(this.sortById);
      this.coveryear_ids = this.coveryears.map((coveryear) => coveryear.id_coveryear)
    },
    handleRowUpdate(row, k, v) {
      const index = this.coveryear_member_data.findIndex(
        (element) => element.id_member === row.id_member
      );
      this.coveryear_member_data[index][k] = v;
      if (k === "declarer") {
        this.selected_members[this.coveryear_member_data[index].id_member] = {
          ...this.coveryear_member_data[index],
        };
      }
    },
    selectAllMembers() {
      this.selectAll = !this.selectAll;
      this.coveryear_member_data.forEach((el) => {
        el["declarer"] = this.selectAll;
      });
      this.selected_members = this.coveryear_member_data.reduce(
        (accumulator, coveryear_member) => {
          accumulator[coveryear_member.id_member] = { ...coveryear_member };
          return accumulator;
        },
        {}
      );
    },
    handleCoverYearIdChange(coveryear_id){
      this.selected_coveryear_id = coveryear_id;
    },
    async fetchCoverYearMember(fetch_init_data = false) {
      const query = {};
      if (!this.selected_coveryear_id) {
        return;
      }
      Object.values(this.filter_table_data).forEach((column_data) => {
        if (column_data.value && column_data.value !== "") {
          query[`search_${column_data.field}`] = column_data.value;
        }
      });
      const { data } = await this.$request({
        url: COVERYEARS_ENDPOINTS.all_coveryear_member(this.selected_coveryear_id),
        query: {
          per_page: 10000,
          ...query,
        },
      });
      this.coveryear_member_data = data.members;
      this.has_members = data.has_members;
      if (fetch_init_data) {
        this.coveryear_member_data
          .filter((coveryear_member) => coveryear_member.declarer)
          .forEach((coveryear_member) => {
            this.selected_members[coveryear_member.id_member] = {
              ...coveryear_member,
            };
          });
      }
    },
    async filterData() {
      await this.fetchCoverYearMember();
    },
    handleOnProgress(value) {
      this.uploadProgress = value;
    },
    async handleTaskResult(step) {
      this.uploadingFiles = false;
      this.uploadStep = step;
      await this.fetchCoverYearMember(true);
    },
    async uploadFile(file) {
      this.uploadingFiles = true;
      this.uploadError = "";
      this.uploadProgress = 0;
      this.uploadStep = "upload";
      this.uploadingFileName = file.name;
      this.showUploadModal = true;

      const query = {
        id_coveryear: this.selected_coveryear_id,
      };
  
      const response = await this.$uploadFile({
        fileId: 120,
        file: file,
        url: this.COVERYEARS_ENDPOINTS.inject_coveryear_members,
        query: query,
        onProgress: this.handleOnProgress,
        showAlerts: false,
      });
      this.uploadStep = "inject";
      if (response && !response?.error) {
        const task = new Task(response);
        task.on("result", (result) => {
          if (result?.error) {
            this.handleTaskResult("error");
            this.uploadError = result?.error;
          } else this.handleTaskResult("success");
        });

        task.on("error", (error) => {
          this.handleTaskResult("error");
          try { 
            this.uploadError = JSON.stringify(error);
          } catch (e) {
            this.uploadError = error;
          }
        });
      } else {
        this.uploadingFiles = false;
        this.uploadStep = "error";
        this.uploadError = response?.error;
      }
    },
    async handleUpload(files) {
      await Promise.all(Array.from(files).map((file) => this.uploadFile(file)));
    },
    async updateCoveryearMembers() {
      this.showModal = false;
      this.updating_coveryear = true;
      const coveryearMembers = Object.values(this.selected_members)
        .filter((member) => member.declarer)
        .map((member) => ({
          ...member,
          group_ccr: this.coveryear_member_data.find(
            (coveryear_member) => coveryear_member.id_member === member.id_member
          )?.group_ccr,
        }));
      const payload = {
        coveryear_members: coveryearMembers,
        user_creation: ActiveUser?.user?.email,
      };

      const { res } = await this.$request({
        url: COVERYEARS_ENDPOINTS.update_coveryear_member(this.selected_coveryear_id),
        method: "POST",
        body: payload,
      });

      if (res.status === 200) {
        alert(this.$t("Saved Cover Year Members"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })

        await this.fetchCoverYearMember();
      } else {
        alert(this.$t("Something went wrong"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })

      }
      this.updating_coveryear = false;
    },
  },
};
</script>

<style lang="scss">
.mz-select-wrapper {
  width: 90%;
}
</style>
